import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import Step01 from "./Step01";
import { FormProvider, useForm } from "react-hook-form";
import Step02 from "./Step02";
import { useMutation, useQuery } from "react-query";
import axiosClient from "../../../../lib/axios";
import moment, { Moment } from "moment";
import { AuthContext } from "../../../../contexts/AuthContext";
import DialogAppBar from "../../../../components/DialogAppBar";
import { IDataLojas } from "../../../../types/api/geral/pesquisar-lojas";
import DialogConfirmacaoRessuprimento from "./DialogConfirmacaoRessuprimento";

const steps = ['Selecione os produtos', 'Confirmação'];

export default function SolicitarRessuprimentoDialog({ open, handleClose, onCreate, onFail, handleChangeProcesso }: Props) {

    const { userPerfilComLoja } = useContext(AuthContext)

    const [activeStep, setActiveStep] = useState(0);
    const [openDialogConfirmarcao, setOpenDialogConfirmacao] = useState(false);

    const [filter, setFilter] = useState<IFilters>({
        limit: 50,
        page: 0,
        palavra_chave: "",
        periodo_vendas_inicio: moment().subtract(1, 'day').subtract(1, 'year'),
        periodo_vendas_fim: moment().subtract(1, 'day'),
        sazonalidade_inicio: moment().subtract(1, 'day').subtract(1, 'year'),
        sazonalidade_fim: moment().subtract(1, 'day').subtract(1, 'year').add('3', 'months'),
        sazonalidades_ids: null,
        qtdSuggestion: 30,
        origem: null,
        destino: userPerfilComLoja?.loja || null,
        predSelecionado: null,
        mostrar_itens_sem_estoque_origem: false,
        niveis_estoque: ["0a15"]
    })

    const methods = useForm<Inputs>({
        defaultValues: {
            items: [],
            descricao: "",
            observacao: "",
            nao_sugeridos: [],
            filtros: {
                origem: null
            }
        }
    });
    const { setValue, watch, reset, getValues, handleSubmit } = methods;

    //queries e mutations
    const { data } = useQuery({
        queryKey: ["pesquisar-lojas-autofill-ressuprimento"],
        queryFn: () => axiosClient.get("/pesquisar-lojas", {
            params: {
                limit: 1
            }
        })
    });
    const { dataLojaAutofill }: ILojaAutofill = {
        dataLojaAutofill: data?.data.data[0] || null
    }
    //

    // Funções Manipuladoras
    const { mutate: iniciarProcesso, isLoading } = useMutation({
        mutationKey: 'solicitar-ressuprimento',
        mutationFn: (inputs: object) => axiosClient.post("/estoque/solicitar-ressuprimento", inputs),
        onError: (error) => {
            console.error(error)
            onFail()
        },
        onSuccess: ({ data }) => {
            handleChangeProcesso(data)
            setActiveStep(0);
            reset();
            onCreate(data);
        }
    })

    const handleChange = (items: Array<InputProduto>) => {
        setValue('filtros', { origem: filter.origem.id })
        setValue('items', items);
    }

    const handleChangeStep = useCallback((step: number) => {
        if (activeStep === 0 && (watch('items').length <= 0 || watch('items').filter(i => i.quantidade <= 0).length > 0)) {
            setOpenDialogConfirmacao(true);
            return;
        }

        setActiveStep(step)
    }, [activeStep, watch])

    const onSubmit = () => {
        const {
            descricao,
            items,
            observacao,
            nao_sugeridos
        } = getValues();

        const {
            origem,
            destino,
            periodo_vendas_inicio,
            periodo_vendas_fim,
            sazonalidade_inicio,
            sazonalidade_fim,
            sazonalidades_ids,
        } = filter

        const params = {
            origem_loja_id: origem?.id,
            destino_loja_id: destino?.id,
            periodo_inicio: moment(periodo_vendas_inicio).format('YYYY-MM-DD'),
            periodo_fim: moment(periodo_vendas_fim).format('YYYY-MM-DD'),
            sazonalidade_inicio: moment(sazonalidade_inicio).format('YYYY-MM-DD'),
            sazonalidade_fim: moment(sazonalidade_fim).format('YYYY-MM-DD'),
            sazonalidades_ids,
            descricao,
            observacao,
            items: [...items, ...nao_sugeridos]
        }

        iniciarProcesso(params);
    }
    //

    useEffect(() => {
        if (dataLojaAutofill) {
            setFilter(prevState => {
                const params: any = { origem: dataLojaAutofill }
                if (!prevState.destino) params.destino = dataLojaAutofill

                return ({ ...prevState, ...params })
            })
        }
    }, [dataLojaAutofill])

    return (
        <Dialog
            open={open}
            onClose={!isLoading ? handleClose : () => { }}
            maxWidth={activeStep === 0 ? "lg" : "md"}
            fullWidth
            fullScreen
        >
            <DialogAppBar
                {...{ handleClose }}
                title="Solicitar ressuprimento"
            />
            <DialogContent>
                <FormProvider {...methods}>
                    <Stepper activeStep={activeStep}>
                        {steps.map((label, index) => {
                            return (
                                <Step key={index}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>

                    <>
                        <Step01
                            {...{ filter, setFilter }}
                            show={activeStep === 0}
                            onChange={handleChange}
                            selecionados={watch('items')}
                        />

                        <Step02
                            show={activeStep === 1}
                        />
                    </>
                </FormProvider>
            </DialogContent>
            {
                (activeStep === 0 && watch('items').length > 0) &&
                <Box mr={2} mt={2} display="flex" flexDirection="column" alignItems="flex-end">
                    <Typography
                        variant="overline"
                        alignSelf="flex-end"
                    >
                        {watch('items').length} produto(s) selecionados
                    </Typography>
                </Box>
            }

            {
                activeStep === 0 &&
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={() => handleChangeStep(1)}>Avançar</Button>
                </DialogActions>
            }

            {
                activeStep === 1 &&
                <DialogActions>
                    {!isLoading && <Button onClick={() => handleChangeStep(0)}>Voltar</Button>}
                    <Button
                        disabled={isLoading}
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                    >
                        {isLoading && <CircularProgress size={14} style={{ marginRight: 12 }} />} Enviar solicitação
                    </Button>
                </DialogActions>
            }
            {
                openDialogConfirmarcao &&
                <DialogConfirmacaoRessuprimento
                    open={openDialogConfirmarcao}
                    onCancel={() => setOpenDialogConfirmacao(false)}
                    onConfirm={() => {
                        setOpenDialogConfirmacao(false);
                        setActiveStep(1);
                    }}
                />
            }

        </Dialog>
    )
}

interface Props {
    open: boolean,
    handleClose: () => void,
    onCreate: (codigo: string) => void
    onFail: () => void,
    handleChangeProcesso: (item: string) => void
}

export type InputProduto = {
    codigo: number,
    nome: string,
    quantidade: number,
    departamento: string,
    setor: string,
    encomenda: number | null,
    nao_sugerido?: boolean
}

export type Inputs = {
    filtros: {
        origem: number | null
    },
    origem_loja_id: number | null;
    destino_loja_id: number | null;
    periodo_inicio: string | null;
    periodo_fim: string | null;
    sazonalidade_inicio: string | null;
    sazonalidade_fim: string | null;
    items: Array<InputProduto>;
    descricao: string;
    observacao: string;
    nao_sugeridos: Array<InputProduto>;
}

export interface IFilters {
    limit: number;
    page: number;
    palavra_chave: string,
    periodo_vendas_inicio: Moment;
    periodo_vendas_fim: Moment;
    sazonalidade_inicio: Moment;
    sazonalidade_fim: Moment;
    sazonalidades_ids: number[] | null;
    qtdSuggestion: number;
    origem: any;
    destino: any;
    predSelecionado: FiltrosPreDefinidos | null
    departamentos?: Array<{
        id: number,
        nome: string
    }>,
    mostrar_itens_sem_estoque_origem: boolean,
    niveis_estoque: Array<string>
}

export interface FiltrosPreDefinidos {
    nome: string,
    filtros: {
        origem: {
            id: number,
            nome: string
        },
        departamentos: Array<{
            id: number,
            nome: string
        }>
    }
}

interface ILojaAutofill {
    dataLojaAutofill: IDataLojas | null
}